import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { getGroupUsers } from 'core/crud/groups.crud';
import { useGroups } from 'core/hooks/useGroups';
import * as coreEntities from 'core/store/index';
import { parseUsers } from 'core/store/parsers';
import { filterStudents } from 'core/utils/user';

import type { StudentInfo } from './types';

type UseStudentsMapReturn = {
  studentsMap: Map<string, StudentInfo>;
  loading: boolean;
};

function useStudentsMap(): UseStudentsMapReturn {
  const { groups } = useGroups();
  const groupsLoading = useSelector((state: unknown) => coreEntities.groups.selectors.getGroupsLoading(state));
  const user = useSelector((state: unknown) => coreEntities.auth.selectors.getUser(state));

  const [studentsMap, setStudentsMap] = useState<Map<string, StudentInfo>>(new Map());
  const [fetching, setFetching] = useState<boolean>(false);

  const schoolGuid = (user as any)?.schools?.[0]?.guid || null;

  const groupEntries = useMemo(() => {
    const schoolGroups = groups?.schoolGroups || [];
    return schoolGroups.map((g: { guid: string; name: string }) => ({
      guid: g.guid,
      name: g.name,
    }));
  }, [groups]);

  const fetchStudents = useCallback(async () => {
    if (!schoolGuid || groupEntries.length === 0) return;

    setFetching(true);
    const map = new Map<string, StudentInfo>();

    try {
      const promises = groupEntries.map((entry) => getGroupUsers({ schoolGuid, groupGuid: entry.guid }));
      const responses = await Promise.all(promises);

      responses.forEach((response: any, index: number) => {
        const groupName = groupEntries[index].name;
        const users = parseUsers(response?.data?.data?.users || []);
        const students = filterStudents(users);

        students.forEach((student: any) => {
          if (student.guid && !map.has(student.guid)) {
            map.set(student.guid, {
              name: student.name || '',
              lastName: student.lastName || student.lastname || '',
              avatar: student.avatar || null,
              groupName,
            });
          }
        });
      });
    } catch (err) {
      console.error('[useStudentsMap] Error fetching group users:', err);
    }

    setStudentsMap(map);
    setFetching(false);
  }, [schoolGuid, groupEntries]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  return { studentsMap, loading: !!groupsLoading || fetching };
}

export default useStudentsMap;
